import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  memberContactSchema,
  type MemberContact,
} from '../lib/members';

type AdminExtra = { email: string; status: 'active' | 'former' };

type FormValues = MemberContact & Partial<AdminExtra>;

interface SelfProps {
  initial: MemberContact;
  mode?: 'self';
  email?: never;
  status?: never;
  onSave: (values: MemberContact) => Promise<void>;
}

interface AdminProps {
  initial: MemberContact;
  mode: 'admin';
  email: string;
  status: 'active' | 'former';
  onSave: (values: MemberContact & AdminExtra) => Promise<void>;
}

type Props = SelfProps | AdminProps;

const labels = {
  firstName: 'Förnamn',
  lastName: 'Efternamn',
  address: 'Adress',
  postalCode: 'Postnummer',
  city: 'Postort',
  alternateAddress: 'Alternativ adress',
  phoneLandline: 'Telefon (fast)',
  phoneMobile: 'Telefon (mobil)',
};

const requiredMessages: Partial<Record<keyof MemberContact, string>> = {
  firstName: 'Förnamn krävs',
  lastName: 'Efternamn krävs',
  address: 'Adress krävs',
  postalCode: 'Postnummer krävs',
  city: 'Postort krävs',
};

export default function MemberForm(props: Props) {
  const isAdmin = props.mode === 'admin';
  const defaultValues: FormValues = isAdmin
    ? { ...props.initial, email: props.email, status: props.status }
    : props.initial;

  const {
    register,
    handleSubmit,
    control,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(memberContactSchema),
    defaultValues,
  });
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  async function onSubmit(values: FormValues) {
    setServerError(null);
    try {
      // Zod resolver only passes MemberContact fields through; for admin mode
      // we re-read email + status directly from RHF state via getValues().
      const payload: FormValues = isAdmin
        ? { ...values, email: getValues('email'), status: getValues('status') }
        : values;
      await (props.onSave as (v: FormValues) => Promise<void>)(payload);
      setSavedAt(new Date());
    } catch (e) {
      setServerError(
        e instanceof Error ? e.message : 'Kunde inte spara. Försök igen.',
      );
    }
  }

  function fieldError(name: keyof MemberContact): string | undefined {
    const e = errors[name];
    if (!e) return undefined;
    const err = e as { message?: string; type?: string };
    if (err.type === 'too_small' && requiredMessages[name]) {
      return requiredMessages[name];
    }
    return err.message ?? requiredMessages[name] ?? 'Ogiltigt värde';
  }

  function field(
    name: keyof typeof labels,
    extra: { type?: string; nullable?: boolean } = {},
  ) {
    const error = fieldError(name as keyof MemberContact);
    return (
      <label className="block">
        <span className="text-sm font-medium">{labels[name]}</span>
        <input
          type={extra.type ?? 'text'}
          className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-sky focus:ring-sky"
          {...register(name, {
            setValueAs: (v) =>
              extra.nullable && (v === '' || v === null) ? null : v,
          })}
        />
        {error && <span className="text-bad text-sm">{error}</span>}
      </label>
    );
  }

  const sectionTitle =
    'mt-4 mb-2 text-sm font-semibold tracking-wider text-navy uppercase border-b pb-1';

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-xl">
      {field('firstName')}
      {field('lastName')}
      {field('address')}
      <div className="grid grid-cols-2 gap-4">
        {field('postalCode')}
        {field('city')}
      </div>
      {field('alternateAddress', { nullable: true })}
      <div className="grid grid-cols-2 gap-4">
        {field('phoneLandline', { nullable: true })}
        {field('phoneMobile', { nullable: true })}
      </div>

      {isAdmin && (
        <>
          <label className="block">
            <span className="text-sm font-medium">E-post</span>
            <input
              type="email"
              className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-sky focus:ring-sky"
              {...register('email', { required: 'E-post krävs' })}
            />
            {errors.email?.message && (
              <span className="text-bad text-sm">{errors.email.message as string}</span>
            )}
          </label>
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium">Status</legend>
            <label className="flex items-center gap-2">
              <input type="radio" value="active" {...register('status')} />
              <span className="text-sm">Aktiv</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" value="former" {...register('status')} />
              <span className="text-sm">Inaktiv</span>
            </label>
          </fieldset>
        </>
      )}

      <Controller
        control={control}
        name="spouse"
        render={({ field: spouseField, fieldState }) => (
          <div className="space-y-2">
            <h2 className={sectionTitle}>Ytterligare delägare</h2>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={spouseField.value !== null}
                onChange={(e) =>
                  spouseField.onChange(
                    e.target.checked
                      ? { name: '', email: null, phone: null }
                      : null,
                  )
                }
              />
              <span className="text-sm">Det finns en ytterligare delägare på platsen</span>
            </label>
            {spouseField.value !== null && (
              <div className="space-y-2 pl-6">
                <label className="block">
                  <span className="text-sm font-medium">Namn</span>
                  <input
                    type="text"
                    className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-sky focus:ring-sky"
                    value={spouseField.value.name}
                    onChange={(e) =>
                      spouseField.onChange({
                        ...spouseField.value!,
                        name: e.target.value,
                      })
                    }
                  />
                  {fieldState.error?.message && (
                    <span className="text-bad text-sm">Namn krävs</span>
                  )}
                </label>
                <label className="block">
                  <span className="text-sm font-medium">E-post</span>
                  <input
                    type="email"
                    className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-sky focus:ring-sky"
                    value={spouseField.value.email ?? ''}
                    onChange={(e) =>
                      spouseField.onChange({
                        ...spouseField.value!,
                        email: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium">Telefon</span>
                  <input
                    type="text"
                    className="mt-1 block w-full rounded border-gray-300 shadow-sm focus:border-sky focus:ring-sky"
                    value={spouseField.value.phone ?? ''}
                    onChange={(e) =>
                      spouseField.onChange({
                        ...spouseField.value!,
                        phone: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </label>
              </div>
            )}
          </div>
        )}
      />

      {serverError && <p className="text-bad text-sm">{serverError}</p>}
      {savedAt && (
        <p className="text-ok text-sm" role="status">
          Sparat {savedAt.toLocaleTimeString('sv-SE')}
        </p>
      )}
      <button
        type="submit"
        disabled={isSubmitting}
        className="bg-sky text-white rounded px-4 py-2 font-medium disabled:opacity-50"
      >
        {isSubmitting ? 'Sparar…' : 'Spara'}
      </button>
    </form>
  );
}
